"use client"

import {
  DotsGrid,
  ImageX,
  RefreshCw01,
  Repeat01,
  Trash01,
} from "@untitledui/icons"
import type { PointerEvent } from "react"

import { Badge, BadgeWithIcon } from "@/components/ui/badge"
import { ButtonUtility } from "@/components/ui/button-utility"
import { EditableText } from "@/components/ui/editable-text"
import { cn } from "@/lib/utils"
import type { ContentBody, Tag } from "@/types"

import { ClaimTrackEditSlideout } from "./claim-track-edit-slideout"

export interface ClaimTrack {
  id: number
  position: number
  playlist_id: number
  notes: string | null
  is_owner: boolean
  post: {
    id: number
    title: string
    creator: string | null
    canonical_url: string | null
    thumbnail_url: string | null
    content_body_type: string | null
    published: boolean
    /** The content body's editable fields, seeding the edit slideout. */
    content_body_attributes: ContentBody
  }
  tags: unknown[]
}

/**
 * How a board mutation authorises and returns: the recipient's claim bearer
 * token, the coordinator's warm-start id, or — in a normal owner/admin edit —
 * a `return` marker. Spread into every nested track endpoint call; ClaimReturn
 * reads it to route the redirect back to the right editor (?claim →
 * /playlists/:id/edit, ?warm_start → /admin/warm_starts/:id/edit, ?return=edit →
 * /playlists/:id/edit, ?return=admin_edit → /admin/playlists/:id/edit).
 */
export type TrackPersistParams =
  | { claim: string }
  | { warm_start: number }
  | { return: "edit" | "admin_edit" }

interface ClaimTrackCardProps {
  track: ClaimTrack
  index: number
  /** The live (optimistic) value of the track's own note. */
  note: string
  onNoteChange: (value: string) => void
  onNoteCommit: () => void
  onRemove: () => void
  /** Repost only — drop this track and focus the add-a-link input. */
  onReplace: () => void
  /** Drag handle press → starts the reorder drag (from the board's controls). */
  onHandlePointerDown: (event: PointerEvent) => void
  isDragging?: boolean
  /** Playlist slug or id + persistence params + tag options for the slideout. */
  pid: number | string
  persistParams: TrackPersistParams
  availableTags: Tag[]
}

/**
 * A single track in the playlist column of "Make it yours" — the seeded
 * substance of the playlist made tangible. Drag handle, content, the
 * recipient's own inline note, and a forgiving remove. The growth manager's
 * margin note for this track does NOT live here; it sits in the editor's margin
 * column (see claim-margin.tsx), so the playlist column reads uninterrupted.
 */
export function ClaimTrackCard({
  track,
  index,
  note,
  onNoteChange,
  onNoteCommit,
  onRemove,
  onReplace,
  onHandlePointerDown,
  isDragging = false,
  pid,
  persistParams,
  availableTags,
}: ClaimTrackCardProps) {
  const { post } = track
  const typeLabel = post.content_body_type?.replace("Body", "")

  return (
    <div
      className={cn(
        "border-secondary bg-primary flex gap-3 rounded-xl border p-3 transition-shadow duration-200 sm:gap-4 sm:p-4",
        isDragging ? "shadow-lg" : "shadow-xs",
      )}
    >
      {/* Drag handle — the only drag initiator, so the note stays selectable. */}
      <button
        type="button"
        aria-label={`Reorder ${post.title}`}
        onPointerDown={onHandlePointerDown}
        className="text-quaternary hover:text-tertiary -ml-1 flex w-6 shrink-0 cursor-grab touch-none items-center justify-center self-stretch rounded-md transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 active:cursor-grabbing"
      >
        <DotsGrid className="size-5" />
      </button>

      {/* Index */}
      <div className="text-quaternary w-4 shrink-0 self-center text-center text-lg font-semibold tabular-nums">
        {index}
      </div>

      {/* Thumbnail */}
      <div className="bg-secondary_subtle size-14 shrink-0 self-center overflow-hidden rounded-lg sm:size-16">
        {post.thumbnail_url ? (
          <img
            src={post.thumbnail_url}
            alt=""
            className="size-full object-cover"
          />
        ) : (
          <div className="flex size-full items-center justify-center bg-gray-500">
            <ImageX className="size-7 text-white/30" />
          </div>
        )}
      </div>

      {/* Content + the recipient's own note */}
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex min-w-0 flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <p className="text-primary truncate font-semibold">{post.title}</p>
            {typeLabel && (
              <Badge size="sm" color="gray" type="modern" className="shrink-0">
                {typeLabel}
              </Badge>
            )}
            {/* A post the recipient doesn't own is a repost — theirs to keep,
                credit (the creator line below), or swap, but never to rewrite. */}
            {!track.is_owner && (
              <BadgeWithIcon
                size="sm"
                color="gray"
                type="modern"
                iconLeading={Repeat01}
                className="shrink-0"
              >
                Reposted
              </BadgeWithIcon>
            )}
          </div>
          {post.creator && (
            <p className="text-tertiary truncate text-sm">{post.creator}</p>
          )}
        </div>

        {/* The recipient's own note — the "why", in the same editable material
            as the title and description (see docs/design/edit-surfaces.md). */}
        <EditableText
          presentation="body"
          bleed={false}
          ariaLabel={`Note for ${post.title}`}
          value={note}
          onChange={onNoteChange}
          onCommit={onNoteCommit}
          placeholder="Add a note — why this one?"
        />
      </div>

      {/* Actions — owned: edit the post's details; repost: swap the source.
          Both keep a forgiving remove. */}
      <div className="flex shrink-0 flex-col items-center gap-1">
        {track.is_owner ? (
          <ClaimTrackEditSlideout
            track={track}
            pid={pid}
            persistParams={persistParams}
            availableTags={availableTags}
          />
        ) : (
          <ButtonUtility
            size="xs"
            color="tertiary"
            tooltip="Replace with your own"
            icon={RefreshCw01}
            onClick={onReplace}
          />
        )}
        <ButtonUtility
          size="xs"
          color="tertiary"
          tooltip="Remove"
          icon={Trash01}
          onClick={onRemove}
        />
      </div>
    </div>
  )
}
